import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "../../../design-system";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import UploadDownloadArea from "./UploadDownloadArea";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * Letter O — "Show, don't explain."
 *
 * No metaphor to decode: the hero simply IS the real, clickable editor. LEFT is a plain-spoken
 * headline + three honest proof points; RIGHT is Letter N's live ET (embedded, with zoom + a
 * Desktop/Phone toggle + the demo-video picker); the real upload box sits full-width UNDER
 * everything. The one deliberate flourish is a cherry "highlighter" swipe on the headline's key
 * word — an echo of the ET's real Pen/highlight tool — so cherry reads as "the kept moment."
 *
 * Every claim is honest to ET_BEHAVIOR_SPEC.md: sentence→clip rows, filter by speaker/emotion/
 * search, single-clip 1-click download, and stitched export are all real features of the editor.
 */

const font = "Outfit, sans-serif";

// Phone preview: render the ET at a true phone viewport (below its 500px xs breakpoint) so it
// shows the genuine mobile layout, then scale it into a phone-sized frame (mirrors Letter N).
const PHONE_VIEWPORT_W = 390;
const PHONE_FRAME_W = 288;
const PHONE_FRAME_H = 560;
const PHONE_SCALE = PHONE_FRAME_W / PHONE_VIEWPORT_W;
const DESKTOP_ZOOM_DEFAULT = 0.55; // shrink the full-width editor so every spreadsheet column shows
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const iframeStyle = (scale: number): React.CSSProperties => ({
  position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
  transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
});
const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "14px", lineHeight: 1, px: "8px", py: "3px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };

/**
 * The signature flourish: a cherry highlighter swipe behind the key word, sitting across the
 * lower third of the text like a real marker stroke (a nod to the ET's Pen/highlight tool).
 */
const Mark: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      whiteSpace: "nowrap",
      color: "#fff",
      backgroundImage: "linear-gradient(105deg, rgba(230,29,80,0.55), rgba(230,29,80,0.32))",
      backgroundRepeat: "no-repeat",
      backgroundSize: "100% 40%",
      backgroundPosition: "0 82%",
      borderRadius: "3px",
      padding: "0 0.06em",
    }}
  >
    {children}
  </Box>
);

// A few value props to try — cycle with the dots, or hover to pin. All backed by real features.
const HEADLINES: { head: React.ReactNode; sub: string }[] = [
  { head: <>Every sentence is a <Mark>clip.</Mark></>, sub: "We cut your whole video into rows. You keep only the best ones." },
  { head: <>Grab any clip in <Mark>one click.</Mark></>, sub: "Hit the ↓ on any row, or tick a few and export them stitched together." },
  { head: <>Your footage, fully <Mark>searchable.</Mark></>, sub: "Search a word and get the exact clip. Filter by speaker, emotion, or length." },
  { head: <>The good moments, <Mark>cherry-picked.</Mark></>, sub: "Sort, filter, and tick the winners — or let the AI pick them for you." },
];

const headlineSx = {
  fontFamily: font,
  fontWeight: 800,
  color: "#f3f4f9",
  lineHeight: 1.02,
  letterSpacing: "-0.03em",
  fontSize: { xs: "38px", sm: "48px", md: "60px" },
};

const RotatingHead: React.FC = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % HEADLINES.length), 4800);
    return () => window.clearInterval(t);
  }, [paused]);
  const c = HEADLINES[i];
  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{ display: "flex", flexDirection: "column", gap: { xs: "14px", md: "18px" }, alignItems: { xs: "center", md: "flex-start" }, textAlign: { xs: "center", md: "left" } }}
    >
      <Typography sx={{ ...headlineSx, minHeight: { md: "128px" } }}>{c.head}</Typography>
      <Typography sx={{ fontFamily: font, fontWeight: 500, fontSize: { xs: "16px", md: "19px" }, lineHeight: 1.5, color: "#c2c7d6", maxWidth: "480px", minHeight: { md: "58px" } }}>
        {c.sub}
      </Typography>
      <Box sx={{ display: "flex", gap: "8px" }}>
        {HEADLINES.map((_, n) => (
          <Box
            key={n}
            component="button"
            onClick={() => setI(n)}
            title="Try another one"
            aria-label={`Value prop ${n + 1}`}
            sx={{ cursor: "pointer", width: n === i ? 26 : 9, height: 9, borderRadius: "999px", border: "none", p: 0, background: n === i ? "#e61d50" : "rgba(255,255,255,0.22)", transition: "all .25s", "&:focus-visible": { outline: "2px solid #e61d50", outlineOffset: "3px" } }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Three honest, concrete proofs — the things the editor on the right actually does.
const PROOFS = [
  "Every sentence becomes its own clip",
  "Filter by speaker, emotion, or a word",
  "Download one, or export a stitched reel",
];

const ProofList: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "11px", alignItems: { xs: "center", md: "flex-start" } }}>
    {PROOFS.map((p) => (
      <Box key={p} sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
        <Box aria-hidden sx={{ flexShrink: 0, width: 20, height: 20, borderRadius: "6px", background: "rgba(230,29,80,0.14)", border: "1px solid rgba(230,29,80,0.5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#e61d50", fontSize: "12px", fontWeight: 800, lineHeight: 1 }}>✓</Box>
        <Typography sx={{ fontFamily: font, fontSize: { xs: "14px", md: "15px" }, fontWeight: 500, color: "#c2c7d6" }}>{p}</Typography>
      </Box>
    ))}
  </Box>
);

/** The real ET (Letter N's editor), embedded live. Desktop = scaled full editor; Phone = framed. */
const LiveSpreadsheet: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState<"desktop" | "phone">("desktop");
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [zoom, setZoom] = useState(DESKTOP_ZOOM_DEFAULT);
  const [fs, setFs] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "500px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // The demo-video picker (shared with Letters B/K/N) announces a new /s/ src here.
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  const src = inView ? shareSrc : "";
  const switchMode = (m: "desktop" | "phone") => { setMode(m); setLoaded(false); };

  const toggleBtn = (m: "desktop" | "phone", label: string, Icon: React.ElementType) => {
    const active = mode === m;
    return (
      <Box
        component="button"
        onClick={() => switchMode(m)}
        sx={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer", background: active ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent", color: active ? "#fff" : "#c2c7d6", border: "none", fontFamily: font, fontWeight: 700, fontSize: "12.5px", px: "13px", py: "7px" }}
      >
        <Icon sx={{ fontSize: 15 }} /> {label}
      </Box>
    );
  };

  const loadingOverlay = (
    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
      <img src={logoSite} alt="" width={36} style={{ opacity: 0.85 }} />
      <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
    </Box>
  );

  return (
    <Box ref={ref} sx={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      <Box sx={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 40px 90px -55px rgba(0,0,0,0.9), 0 0 0 1px rgba(230,29,80,0.10)", background: "#0b0a0f" }}>
        {/* window title bar — reads like an app, with a live dot + the Desktop/Phone toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.55 }} />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: "#e61d50", boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9", display: { xs: "none", sm: "block" } }}>
              Film Reel — live
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {mode === "desktop" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
                <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#c2c7d6", px: "5px", minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
                <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
              </Box>
              <Box component="button" onClick={() => setFs(true)} title="Open full screen" sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "11px", px: "8px", py: "4px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
                <OpenInFullIcon sx={{ fontSize: 13 }} /> Full screen
              </Box>
            </>
          )}
          <Box sx={{ display: "inline-flex", border: "1px solid #3a2b46", borderRadius: 999, overflow: "hidden" }}>
            {toggleBtn("desktop", "Desktop", DesktopWindowsIcon)}
            {toggleBtn("phone", "Phone", PhoneIphoneIcon)}
          </Box>
        </Box>

        {mode === "desktop" ? (
          <Box sx={{ position: "relative", height: { xs: "440px", md: "556px" } }}>
            {src ? (
              <iframe
                title="CherryClipz — live spreadsheet"
                src={src}
                allow="fullscreen; clipboard-write"
                onLoad={() => setLoaded(true)}
                style={iframeStyle(zoom)}
              />
            ) : null}
            {!loaded && loadingOverlay}
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: "22px", height: { xs: "440px", md: "556px" }, alignItems: "center" }}>
            <Box sx={{ position: "relative", width: PHONE_FRAME_W, height: PHONE_FRAME_H, borderRadius: "34px", border: "10px solid #15131b", background: "#0b0a0f", overflow: "hidden", boxShadow: "0 30px 80px -40px rgba(0,0,0,0.9)" }}>
              {src ? (
                <iframe
                  title="CherryClipz — mobile view"
                  src={src}
                  allow="fullscreen; clipboard-write"
                  onLoad={() => setLoaded(true)}
                  style={{ position: "absolute", top: 0, left: 0, border: "none", width: PHONE_VIEWPORT_W, height: Math.round(PHONE_FRAME_H / PHONE_SCALE), transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left" }}
                />
              ) : null}
              {!loaded && loadingOverlay}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8" }}>
          <Box component="span" sx={{ color: "#e61d50" }}>↑</Box> Real, not a screenshot — click any clip to play it.
        </Typography>
        <DemoVideoPicker />
      </Box>

      {/* full-screen — scale to fit so nothing on the ET's edges is cut off */}
      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: "12px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <Box component="button" onClick={() => setFs(false)} title="Close" sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "5px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>Close ×</Box>
            </Box>
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SplitHeroEtPage: React.FC = () => (
  <Box sx={{ padding: { xs: "8px 16px 44px", md: "20px 48px 64px" } }}>
    <Box sx={{ width: "100%", maxWidth: "1480px", margin: "0 auto" }}>
      {/* eyebrow — plain words, a cherry live dot. No code, no symbols. */}
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", mb: { xs: "22px", md: "30px" }, px: "12px", py: "6px", borderRadius: "999px", border: "1px solid rgba(230,29,80,0.35)", background: "rgba(230,29,80,0.08)" }}>
        <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: "#e61d50", boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12px", letterSpacing: "0.02em", color: "#f4c9d6" }}>
          The real editor — live. No login to try.
        </Typography>
      </Box>

      {/* TOP — pitch (left) · live editor (right) */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: "28px", md: "48px" } }}>
        {/* LEFT — the pitch */}
        <Box sx={{ flex: { md: "0 0 40%" }, minWidth: 0, display: "flex", flexDirection: "column", gap: { xs: "22px", md: "28px" }, justifyContent: "center" }}>
          <RotatingHead />
          <ProofList />
        </Box>

        {/* RIGHT — the live editor, the star */}
        <Box sx={{ flex: { md: "1 1 auto" }, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <LiveSpreadsheet />
        </Box>
      </Box>

      {/* BOTTOM — the call to action: the real upload box, full-width under everything */}
      <Box sx={{ mt: { xs: "34px", md: "48px" }, display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: { xs: "17px", md: "19px" }, color: "#f3f4f9", textAlign: "center" }}>
          Start with your own video
        </Typography>
        <Box id="cc-demo-upload" sx={{ width: "100%", maxWidth: "760px" }}><UploadDownloadArea /></Box>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8", textAlign: "center" }}>
          Upload a file or paste a link — pick your clips, export a reel. Longer videos take a little longer.
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default SplitHeroEtPage;
